import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'

const HOURS = Array.from({ length: 24 }, (_, h) => h)

function formatWhen(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function TelegramSettings() {
  const { isAdmin } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [linking, setLinking] = useState(false)
  const [testing, setTesting] = useState(false)
  const [webhookBusy, setWebhookBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [botToken, setBotToken] = useState('')
  const [clearToken, setClearToken] = useState(false)
  const [hasToken, setHasToken] = useState(false)
  const [tokenHint, setTokenHint] = useState(null)
  const [chatId, setChatId] = useState('')
  const [digestEnabled, setDigestEnabled] = useState(false)
  const [digestHour, setDigestHour] = useState(8)
  const [lastSentAt, setLastSentAt] = useState(null)
  const [configured, setConfigured] = useState(false)
  const [webhookActive, setWebhookActive] = useState(false)
  const [todoInbox, setTodoInbox] = useState('Bekat IT → ToDo')

  const apply = useCallback((data) => {
    setHasToken(Boolean(data.has_bot_token))
    setTokenHint(data.bot_token_hint || null)
    setChatId(data.chat_id || '')
    setDigestEnabled(Boolean(data.digest_enabled))
    setDigestHour(
      typeof data.digest_hour === 'number' ? data.digest_hour : 8,
    )
    setLastSentAt(data.last_sent_at || null)
    setConfigured(Boolean(data.configured))
    setWebhookActive(Boolean(data.webhook_active))
    setTodoInbox(data.todo_inbox || 'Bekat IT → ToDo')
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await settingsApi.getTelegramSettings()
      apply(data)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load Telegram settings.',
      )
    } finally {
      setLoading(false)
    }
  }, [apply])

  useEffect(() => {
    if (!isAdmin) return undefined
    load()
    return undefined
  }, [isAdmin, load])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setInfo('')
    try {
      const payload = {
        digest_enabled: digestEnabled,
        digest_hour: digestHour,
        chat_id: chatId.trim() || null,
      }
      if (clearToken) {
        payload.clear_bot_token = true
      } else if (botToken.trim()) {
        payload.bot_token = botToken.trim()
      }
      const updated = await settingsApi.updateTelegramSettings(payload)
      apply(updated)
      setBotToken('')
      setClearToken(false)
      setInfo('Telegram settings saved.')
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save Telegram settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  async function handleLinkChat() {
    setLinking(true)
    setError('')
    setInfo('')
    try {
      if (botToken.trim()) {
        const updated = await settingsApi.updateTelegramSettings({
          bot_token: botToken.trim(),
        })
        apply(updated)
        setBotToken('')
        setClearToken(false)
      }
      const result = await settingsApi.linkTelegramChat()
      setChatId(result.chat_id || '')
      setInfo(result.message || 'Chat linked.')
      await load()
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not link chat.',
      )
    } finally {
      setLinking(false)
    }
  }

  async function handleSendTest() {
    setTesting(true)
    setError('')
    setInfo('')
    try {
      const result = await settingsApi.sendTelegramTest()
      setInfo(result.message || 'Test digest sent.')
      if (result.last_sent_at) setLastSentAt(result.last_sent_at)
      await load()
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not send test digest.',
      )
    } finally {
      setTesting(false)
    }
  }

  async function handleEnableWebhook() {
    setWebhookBusy(true)
    setError('')
    setInfo('')
    try {
      const result = await settingsApi.enableTelegramWebhook()
      setWebhookActive(Boolean(result.webhook_active))
      setInfo(result.message || 'Webhook enabled.')
      await load()
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not enable webhook.',
      )
    } finally {
      setWebhookBusy(false)
    }
  }

  async function handleDisableWebhook() {
    setWebhookBusy(true)
    setError('')
    setInfo('')
    try {
      const result = await settingsApi.disableTelegramWebhook()
      setWebhookActive(Boolean(result.webhook_active))
      setInfo(result.message || 'Webhook cleared.')
      await load()
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not clear webhook.',
      )
    } finally {
      setWebhookBusy(false)
    }
  }

  return (
    <SettingsShell title="Telegram" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Telegram
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Daily digest of open{' '}
            <Link to="/expiries" className="underline">
              ToDo / Expiring
            </Link>{' '}
            items, and guided <strong>/todo</strong> capture into{' '}
            <strong>{todoInbox}</strong>. Production only — the development site
            will not message Telegram or accept webhooks.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {info && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {info}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
        >
          {loading ? (
            <p className="text-sm text-brand-muted">Loading…</p>
          ) : (
            <>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                Status:{' '}
                <strong
                  className={
                    configured ? 'text-emerald-800' : 'text-amber-800'
                  }
                >
                  {configured ? 'Ready' : 'Not linked'}
                </strong>
                {hasToken && tokenHint && (
                  <span className="ml-2 text-brand-muted">
                    Token {tokenHint}
                  </span>
                )}
                <span className="mt-1 block text-xs text-brand-muted">
                  Last digest: {formatWhen(lastSentAt)}
                </span>
                <span className="mt-1 block text-xs text-brand-muted">
                  Webhook:{' '}
                  <strong
                    className={
                      webhookActive ? 'text-emerald-800' : 'text-amber-800'
                    }
                  >
                    {webhookActive ? 'Active (/todo)' : 'Off'}
                  </strong>
                </span>
              </div>

              <ol className="list-decimal space-y-1 pl-5 text-sm text-brand-muted">
                <li>
                  In Telegram, open <strong>@BotFather</strong>, create a bot,
                  and copy the token.
                </li>
                <li>Paste the token below and save.</li>
                <li>
                  Open your bot and send <strong>/start</strong>, then{' '}
                  <strong>Link my chat</strong> (clear the webhook first if it
                  is already active).
                </li>
                <li>Enable the digest and optionally send a test.</li>
                <li>
                  On production, click <strong>Enable webhook</strong> so{' '}
                  <strong>/todo</strong> works.
                </li>
              </ol>

              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-brand-muted">
                <p className="font-medium text-brand">Chat commands</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  <li>
                    <strong>/todo</strong> — asks Customer, Task name, Task,
                    then Due date; creates a row on {todoInbox}
                  </li>
                  <li>
                    <strong>/cancel</strong> — abort the current draft
                  </li>
                </ul>
              </div>

              <label className="block max-w-lg text-sm">
                <span className="text-brand-muted">
                  Bot token {hasToken ? '(leave blank to keep current)' : ''}
                </span>
                <input
                  type="password"
                  value={botToken}
                  onChange={(e) => {
                    setBotToken(e.target.value)
                    if (e.target.value) setClearToken(false)
                  }}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                  autoComplete="off"
                  placeholder={hasToken ? '••••••••' : 'Paste bot token'}
                  disabled={clearToken}
                />
              </label>

              {hasToken && (
                <label className="flex items-center gap-2 text-sm text-brand">
                  <input
                    type="checkbox"
                    checked={clearToken}
                    onChange={(e) => {
                      setClearToken(e.target.checked)
                      if (e.target.checked) setBotToken('')
                    }}
                  />
                  Remove stored bot token
                </label>
              )}

              <div className="flex flex-wrap items-end gap-3">
                <label className="block max-w-xs flex-1 text-sm">
                  <span className="text-brand-muted">Chat id</span>
                  <input
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                    placeholder="Filled by Link my chat"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleLinkChat}
                  disabled={
                    linking ||
                    webhookActive ||
                    (!hasToken && !botToken.trim())
                  }
                  className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {linking ? 'Linking…' : 'Link my chat'}
                </button>
              </div>

              <label className="flex items-center gap-2 text-sm text-brand">
                <input
                  type="checkbox"
                  checked={digestEnabled}
                  onChange={(e) => setDigestEnabled(e.target.checked)}
                />
                Enable daily digest
              </label>

              <label className="block max-w-xs text-sm">
                <span className="text-brand-muted">
                  Send hour (Europe/London)
                </span>
                <select
                  value={digestHour}
                  onChange={(e) => setDigestHour(Number(e.target.value))}
                  className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, '0')}:00
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save Telegram settings'}
                </button>
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={testing || !configured}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {testing ? 'Sending…' : 'Send test digest'}
                </button>
                {!webhookActive ? (
                  <button
                    type="button"
                    onClick={handleEnableWebhook}
                    disabled={webhookBusy || !configured}
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    {webhookBusy ? 'Working…' : 'Enable webhook'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDisableWebhook}
                    disabled={webhookBusy}
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    {webhookBusy ? 'Working…' : 'Clear webhook'}
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      </div>
    </SettingsShell>
  )
}
